import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Tag, Typography, Table, Input, Space, Dropdown, Button, App, Tooltip } from 'antd';
import { MoreOutlined, EyeOutlined, LockOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { deleteClientSubscription, listClientSubcriptions, updateClientSubscriptionStatus } from '../../services/client-subscription';
import moment from 'moment';
import usePermissions from '../../hooks/usePermissions';


const { Title } = Typography;
const { useApp } = App;

const ClientSubscriptionList = () => {
  const navigate = useNavigate();
  const { message, modal } = useApp();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const { can } = usePermissions();

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [sorter, setSorter] = useState({});
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async (page, pageSize, srt, q) => {
    setLoading(true);
    try {
      const { items, total } = await listClientSubcriptions({
        page: page || pagination.current,
        pageSize: pageSize || pagination.pageSize,
        search: q || undefined,
        sortField: srt?.field,
        sortOrder: srt?.order,
      });
      setData(items || []);
      setPagination(prev => ({ 
        ...prev, 
        current: page || prev.current, 
        pageSize: pageSize || prev.pageSize, 
        total,
      }));
    } catch (err) {
      console.error(err);
      message.error('No se pudo cargar la lista de Suscripciones.');
    } finally {
      setLoading(false);
    }
  }, [pagination, message]);

  useEffect(() => {
    fetchData(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (pag, filters, srt) => {
    setSorter(srt || {});
    fetchData(pag.current, pag.pageSize, srt, search);
  };

  const onSearch = (value) => {
    setSearch(value || '');
    // al buscar, reinicia a página 1
    fetchData(1, pagination.pageSize, sorter, value || '');
  };

  // Función para manejar las acciones del menú
  const handleMenuAction = (action, record) => {
    
    switch (action) {
      case 'view':
        navigate(`/subscriptions/${record.id}/view`);
        break;
      case 'toggle':
        const willActivate = record.status === 'CANCELED';
        modal.confirm({
          title: willActivate
            ? '¿Activar suscripción?'
            : '¿Desactivar suscripción?',
          content: `${record.client?.firstName || record.email || 'Suscripción'}`,
          okText: willActivate ? 'Activar' : 'Desactivar',
          cancelText: 'Cancelar',
          onOk: async () => {
            try {
              await updateClientSubscriptionStatus(
                record.id,
                willActivate ? 'ACTIVE' : 'CANCELED',
              );

              message.success(
                willActivate
                  ? 'Suscripción activada'
                  : 'Suscripción desactivada',
              );

              fetchData(
                pagination.current,
                pagination.pageSize,
                sorter,
                search,
              );
            } catch {
              message.error('No se pudo actualizar el estatus');
            }
          },
        });
        break;
      case 'delete':
        modal.confirm({
          title: '¿Eliminar Suscripción?',
          content: `${record.name || record.email || 'Suscripción'}`,
          okText: 'Eliminar',
          okButtonProps: { danger: true },
          cancelText: 'Cancelar',
          onOk: async () => {
            try {
              await deleteClientSubscription(record.id);
              message.success('Suscripción eliminado');

              const nextPage = (data.length === 1 && pagination.current > 1)
                ? pagination.current - 1
                : pagination.current;
              setPagination(prev => ({ ...prev, current: nextPage }));
              fetchData(nextPage, pagination.pageSize, sorter, search);
            } catch {
              message.error('No se pudo eliminar la Suscripción');
            }
          },
        });
        break;
      default:
        break;
    }
  };

  const getActionsMenu = (record) => {
    const menuItems = [
      can('subscriptions.view') && {
        key: 'view',
        icon: <EyeOutlined />,
        label: 'Ver Suscripción',
      },
      ...(record.deleted 
        ? []
        : [
          can('subscriptions.toggle') && {
            key: 'toggle',
            icon: <LockOutlined />,
            label: record.status === 'CANCELED' ? 'Activar' : 'Desactivar',
          },
          can('subscriptions.delete') && {
            key: 'delete',
            icon: <DeleteOutlined />,
            label: 'Borrar Suscripción',
            danger: true,
          },
        ]
      ),
      {
        type: 'divider',
      },
    ];

    return {
      items: menuItems,
      onClick: ({ key }) => handleMenuAction(key, record),
    };
  };

  const columns = [
    { title: 'Nombre Cliente', dataIndex: 'client', key: 'client', sorter: true, render: (text, record) => {
        return `${record.client?.firstName} ${record.client?.lastName} ${record.client?.secondLastName ? `${record.client?.secondLastName}` : ''}`;
      },
    },
    { title: 'Medio', dataIndex: 'medium', key: 'medium', sorter: true },
    { title: 'Plan', dataIndex: 'plan', key: 'plan', sorter: true, render: (text, record) => {
        return `${record.plan?.name}`;
      },
    },
    { title: 'Fecha Inicio', dataIndex: 'startDate', key: 'startDate', sorter: true, render: (text, record) => text? moment(text).format('DD/MM/YYYY') : '-', },
    { title: 'Fecha Vigencia', dataIndex: 'endDate', key: 'endDate', sorter: true, render: (text, record) => text? moment(text).format('DD/MM/YYYY') : '-', },
    { title: 'Precio', dataIndex: 'price', key: 'price', sorter: true, render: (text, record) => {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: record.currency || 'MXN',
        }).format(text);
      }
    },
    { title: 'Total', dataIndex: 'total', key: 'total', sorter: true, render: (text, record) => {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: record.plan?.currency || 'MXN',
        }).format(text);
      }
    },
    {
      title: 'Estatus',
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      render: (_, record) => {
        const labels: Record<string, string> = {
          PENDING: 'Pendiente',
          ACTIVE: 'Activa',
          EXPIRED: 'Expirada',
          CANCELED: 'Cancelada',
          FAILED: 'Fallida',
        };
        return labels[record.status] ?? record.status;
      },
    },
    {
      title: 'Eliminada',
      dataIndex: 'deleted',
      key: 'deleted',
      sorter: true,
      render: (isActive) => (
        <Tag color={!isActive ? 'green' : 'volcano'}>
          {!isActive ? 'Activo' : 'Inactivo'}
        </Tag>
      ),
      // filters: [
      //   { text: 'Activo', value: '1' },
      //   { text: 'Inactivo', value: '0' },
      // ],
      // onFilter: (value, record) => String(record.is_active ? 1 : 0) === String(value),
    },
    {
      title: 'Acciones',
      key: 'actions',
      align: 'center',
      width: 100,
      render: (_, record) => (
        <Dropdown menu={getActionsMenu(record)} trigger={['click']} placement="bottomRight">
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
            style={{ fontSize: '16px' }}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div className="header-container">
        <Title level={3} style={{ marginBottom: 0 }}>
          Suscripciones
        </Title>
        {can('subscriptions.add') && (<Tooltip title="Agregar Suscripción">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            className='btn-add'
            aria-label="Agregar Suscripción"
            onClick={() => navigate('/subscriptions/add')}
          />
        </Tooltip>)}
      </div>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input.Search
            placeholder="Buscar"
            allowClear
            onSearch={onSearch}
            style={{ maxWidth: 360 }}
          />
          <Table
            rowKey={(r) => r.id ?? r._id}
            columns={columns}
            dataSource={data}
            loading={loading}
            scroll={{ x: 'max-content' }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
              showTotal: (total) => `Total: ${total} Suscripciones`
            }}
            onChange={onChange}
          />
        </Space>
      </Card>
    </div>
  );
};

export default ClientSubscriptionList;
